/**
 * RouteRegistry — fallback menu used when /api/me/menu cannot be reached.
 *
 * Per `/memories/critical-lessons.md` ADMIN DECIDES PAGE ACCESS:
 * - This file is NEVER consulted to decide if a route is "admin-only".
 * - This file only provides DEFAULTS for first-boot seeding and offline
 *   fallback. The actual menu the user sees comes from /api/me/menu, which
 *   is admin-controlled via Role Manager.
 */

export interface RegisteredRoute {
  readonly path: string;
  readonly label: string;
  readonly section: string;
  readonly sortOrder: number;
  readonly icon?: string;
}

export const ROUTE_REGISTRY: ReadonlyArray<RegisteredRoute> = [
  // OVERVIEW
  { path: '/dashboard', label: 'Dashboard', section: 'OVERVIEW', sortOrder: 10 },

  // SALES
  { path: '/sales/dashboard', label: 'Sales Dashboard', section: 'SALES', sortOrder: 10 },
  { path: '/sales/jobs', label: 'Sales Jobs', section: 'SALES', sortOrder: 20 },
  { path: '/sales/jobs/new', label: 'New Job', section: 'SALES', sortOrder: 30 },
  { path: '/sales/drafts', label: 'My Drafts', section: 'SALES', sortOrder: 40 },

  // BACK OFFICE
  { path: '/bo/queue', label: 'BO Queue', section: 'BO', sortOrder: 10 },
  { path: '/bo/tat', label: 'TAT Monitoring', section: 'BO', sortOrder: 40 },
  { path: '/bo/clarifications', label: 'Clarifications', section: 'BO', sortOrder: 50 },
  { path: '/inbox', label: 'Approval Inbox', section: 'BO', sortOrder: 20 },
  { path: '/inbox/delegate', label: 'Delegation', section: 'BO', sortOrder: 30 },

  // VENDOR (admin view of vendor master)
  { path: '/admin/vendors', label: 'Vendors', section: 'VENDOR', sortOrder: 10 },
  { path: '/admin/vendors/new', label: 'New Vendor', section: 'VENDOR', sortOrder: 20 },

  // VENDOR OPS (M08 §29 — vendor self-service operations). Job detail and its
  // action sub-pages hang off the dashboard entry (they share its page key).
  { path: '/vendor-ops/dashboard', label: 'Vendor Dashboard', section: 'VENDOR-OPS', sortOrder: 10 },
  { path: '/vendor-ops/clarifications', label: 'My Clarifications', section: 'VENDOR-OPS', sortOrder: 20 },
  { path: '/vendor-ops/performance', label: 'My Performance', section: 'VENDOR-OPS', sortOrder: 30 },
  { path: '/vendor-ops/sla', label: 'SLA Tracking', section: 'VENDOR-OPS', sortOrder: 40 },

  // PAINTER
  {
    path: '/admin/painters/assignments',
    label: 'Painter Assignments',
    section: 'PAINTER',
    sortOrder: 10,
  },

  // MASTER DATA (M05) — master maintenance, governance & bulk tooling.
  // Section defaults to MDM; the admin can re-section any of these via Role
  // Manager. Default-menu entries for M05 (UI Audit M05 — C8 navigation-registry gap).
  { path: '/admin/mdm/lookups', label: 'Lookup Catalogue', section: 'MDM', sortOrder: 10 },
  { path: '/admin/mdm/taxation', label: 'Taxation (GST/HSN)', section: 'MDM', sortOrder: 20 },
  { path: '/admin/mdm/calendars', label: 'Holiday Calendar', section: 'MDM', sortOrder: 30 },
  { path: '/admin/mdm/customers', label: 'Customers / Dealers', section: 'MDM', sortOrder: 40 },
  { path: '/admin/mdm/vendors', label: 'Vendors', section: 'MDM', sortOrder: 50 },
  { path: '/admin/mdm/painters', label: 'Painters', section: 'MDM', sortOrder: 60 },
  { path: '/admin/mdm/work-types', label: 'Work Types', section: 'MDM', sortOrder: 70 },
  { path: '/admin/mdm/rejection-reasons', label: 'Rejection Reasons', section: 'MDM', sortOrder: 80 },
  { path: '/admin/mdm/tat-rules', label: 'TAT Rules', section: 'MDM', sortOrder: 90 },
  { path: '/admin/mdm/import', label: 'Import Console', section: 'MDM', sortOrder: 100 },
  { path: '/admin/mdm/export', label: 'Export Console', section: 'MDM', sortOrder: 110 },
  { path: '/admin/mdm/data-quality', label: 'Data Quality', section: 'MDM', sortOrder: 120 },
  { path: '/admin/mdm/approvals', label: 'Approval Queue', section: 'MDM', sortOrder: 130 },
  { path: '/admin/mdm/version-history', label: 'Version History', section: 'MDM', sortOrder: 140 },
  { path: '/admin/master-config/gst/platform', label: 'Platform GST Configuration', section: 'MDM', sortOrder: 150 },
  { path: '/admin/master-config/gst/company', label: 'Company Tax Configuration', section: 'MDM', sortOrder: 160 },

  // ADMIN (the admin can re-section any of these via Role Manager)
  { path: '/admin/users', label: 'Users', section: 'ADMIN', sortOrder: 10 },
  { path: '/admin/roles', label: 'Roles', section: 'ADMIN', sortOrder: 20 },
  { path: '/admin/roles/matrix', label: 'Permission Matrix', section: 'ADMIN', sortOrder: 30 },
  { path: '/admin/workflows', label: 'Workflows', section: 'ADMIN', sortOrder: 40 },
  // M04 Workflow Engine — authoring hub is /admin/workflows above; these are the
  // read + runtime + library entries (UI Audit M04 — C6 navigation registry gap).
  { path: '/admin/wf/definitions', label: 'Workflow Definitions', section: 'ADMIN', sortOrder: 41 },
  { path: '/admin/wf/templates', label: 'Workflow Templates', section: 'ADMIN', sortOrder: 42 },
  { path: '/admin/wf/instances', label: 'Workflow Instances', section: 'ADMIN', sortOrder: 43 },
  { path: '/admin/wf/escalations', label: 'Workflow Escalations', section: 'ADMIN', sortOrder: 44 },
  { path: '/admin/wf/sla', label: 'Workflow SLA', section: 'ADMIN', sortOrder: 45 },
  { path: '/admin/wf/dashboard', label: 'Workflow Dashboard', section: 'ADMIN', sortOrder: 46 },
  { path: '/admin/wf/delegations', label: 'Workflow Delegations', section: 'ADMIN', sortOrder: 47 },
  {
    path: '/admin/notifications/templates',
    label: 'Notification Templates',
    section: 'ADMIN',
    sortOrder: 50,
  },
  {
    path: '/admin/notifications/history',
    label: 'Notification History',
    section: 'ADMIN',
    sortOrder: 60,
  },
  { path: '/admin/config', label: 'System Config', section: 'ADMIN', sortOrder: 70 },
  { path: '/audit', label: 'Audit Search', section: 'ADMIN', sortOrder: 80 },
  { path: '/audit/logins', label: 'Login Audit', section: 'ADMIN', sortOrder: 90 },
  { path: '/admin/menus', label: 'Menu Master', section: 'ADMIN', sortOrder: 100 },
  { path: '/admin/security/dashboard', label: 'Security Dashboard', section: 'ADMIN', sortOrder: 110 },
  { path: '/admin/security/sessions', label: 'Session Monitor', section: 'ADMIN', sortOrder: 120 },

  // ORGANIZATION (M03) — org / geography / employee / territory masters.
  // Section defaults to ADMIN; the admin can re-section any of these via Role
  // Manager. These are the default-menu entries for M03 (UI Audit M03 — the
  // "no org entries in RouteRegistry" navigation gap).
  { path: '/org/organizations', label: 'Organizations', section: 'ADMIN', sortOrder: 130 },
  { path: '/org/zones', label: 'Zones', section: 'ADMIN', sortOrder: 131 },
  { path: '/org/regions', label: 'Regions', section: 'ADMIN', sortOrder: 132 },
  { path: '/org/states', label: 'States', section: 'ADMIN', sortOrder: 133 },
  { path: '/org/districts', label: 'Districts', section: 'ADMIN', sortOrder: 134 },
  { path: '/org/departments', label: 'Departments', section: 'ADMIN', sortOrder: 135 },
  { path: '/org/designations', label: 'Designations', section: 'ADMIN', sortOrder: 136 },
  { path: '/org/territories', label: 'Territories', section: 'ADMIN', sortOrder: 137 },
  { path: '/org/employees', label: 'Employees', section: 'ADMIN', sortOrder: 138 },
  { path: '/org/transfers', label: 'Transfers', section: 'ADMIN', sortOrder: 139 },
  { path: '/org/successors', label: 'Successor Mapping', section: 'ADMIN', sortOrder: 140 },
  { path: '/org/stakeholders', label: 'Stakeholders', section: 'ADMIN', sortOrder: 141 },
  { path: '/org/leave-replacements', label: 'Leave Replacements', section: 'ADMIN', sortOrder: 142 },
  { path: '/org/hierarchy', label: 'Reporting Hierarchy', section: 'ADMIN', sortOrder: 143 },
  { path: '/org/structure', label: 'Org Structure', section: 'ADMIN', sortOrder: 144 },

  // INTEGRATION PLATFORM (M29 — Enterprise Integration Platform / EIP).
  // Section defaults to INTEGRATION; the admin can re-section any entry via
  // Role Manager. Offline-fallback defaults only — the live, admin-controlled
  // menu comes from /api/me/menu (seed: m29_003_eip_menu_seed.sql).
  { path: '/admin/eip', label: 'Integration Dashboard', section: 'INTEGRATION', sortOrder: 10 },
  { path: '/admin/eip/apis', label: 'API Catalog', section: 'INTEGRATION', sortOrder: 20 },
  { path: '/admin/eip/analytics', label: 'API Analytics', section: 'INTEGRATION', sortOrder: 30 },
  { path: '/admin/eip/connectors', label: 'Connector Management', section: 'INTEGRATION', sortOrder: 40 },
  { path: '/admin/eip/webhooks', label: 'Webhook Management', section: 'INTEGRATION', sortOrder: 50 },
  { path: '/admin/eip/events', label: 'Event Management', section: 'INTEGRATION', sortOrder: 60 },
  { path: '/admin/eip/queue', label: 'Queue Monitoring', section: 'INTEGRATION', sortOrder: 70 },
  { path: '/admin/eip/portal', label: 'Developer Portal', section: 'INTEGRATION', sortOrder: 80 },
  { path: '/admin/eip/sync', label: 'Sync Dashboard', section: 'INTEGRATION', sortOrder: 90 },
  { path: '/admin/eip/health', label: 'Integration Health', section: 'INTEGRATION', sortOrder: 100 },

  // M43 — Multi-Tenant White-Label SaaS Administration & Tenant Governance
  { path: '/admin/saas', label: 'SaaS Administration', section: 'SAAS', sortOrder: 10 },
  { path: '/admin/saas/tenants', label: 'Tenant Management', section: 'SAAS', sortOrder: 20 },
  { path: '/admin/saas/provision', label: 'Tenant Provisioning', section: 'SAAS', sortOrder: 30 },
  { path: '/admin/saas/branding', label: 'Branding Management', section: 'SAAS', sortOrder: 40 },
  { path: '/admin/saas/domains', label: 'Domain Management', section: 'SAAS', sortOrder: 50 },
  { path: '/admin/saas/features', label: 'Feature Management', section: 'SAAS', sortOrder: 60 },
  { path: '/admin/saas/usage', label: 'Usage Monitoring', section: 'SAAS', sortOrder: 70 },
  { path: '/admin/saas/analytics', label: 'Tenant Analytics', section: 'SAAS', sortOrder: 80 },
  { path: '/admin/saas/backups', label: 'Backup & Recovery', section: 'SAAS', sortOrder: 90 },
  { path: '/admin/saas/governance', label: 'Governance', section: 'SAAS', sortOrder: 100 },
  { path: '/admin/saas/audit', label: 'SaaS Audit', section: 'SAAS', sortOrder: 110 },

  // M44 — Billing, Subscription, Commercial Management & Revenue Operations Platform
  { path: '/admin/commercial', label: 'Subscription Dashboard', section: 'BILLING', sortOrder: 10 },
  { path: '/admin/commercial/plans', label: 'Plan Management', section: 'BILLING', sortOrder: 20 },
  { path: '/admin/commercial/bills', label: 'Billing Dashboard', section: 'BILLING', sortOrder: 30 },
  { path: '/admin/commercial/invoices', label: 'Invoice Management', section: 'BILLING', sortOrder: 40 },
  { path: '/admin/commercial/payments', label: 'Payment Tracking', section: 'BILLING', sortOrder: 50 },
  { path: '/admin/commercial/tax', label: 'Tax Configuration', section: 'BILLING', sortOrder: 60 },
  { path: '/admin/commercial/collections', label: 'Collections', section: 'BILLING', sortOrder: 70 },
  { path: '/admin/commercial/analytics', label: 'Revenue Analytics', section: 'BILLING', sortOrder: 80 },
  { path: '/admin/commercial/governance', label: 'Commercial Governance', section: 'BILLING', sortOrder: 90 },
  { path: '/admin/commercial/audit', label: 'Commercial Audit', section: 'BILLING', sortOrder: 100 },
];
